import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Filter, RotateCcw, FileText, Printer, Download as DownloadIcon
} from 'lucide-react';
import { toast } from 'react-toastify';
import salesQuotationService from '../../../services/salesQuotationService';
import customerMasterService from '../../../services/customerMasterService';
import prospectMasterService from '../../../services/prospectMasterService';
import Breadcrumb from '../../../components/Common/Breadcrumb';
import DataTable from '../../../components/DataTable/DataTable';

const SalesQuotationReport = () => {
  const navigate = useNavigate();
  
  // Get current month start and end dates
  const getCurrentMonthDates = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0);
    
    // Format dates as YYYY-MM-DD using local timezone
    const formatDate = (date) => {
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };
    
    return {
      fromDate: formatDate(startDate),
      toDate: formatDate(endDate)
    };
  };

  const [filters, setFilters] = useState({
    customer: '',
    fromDate: getCurrentMonthDates().fromDate,
    toDate: getCurrentMonthDates().toDate,
    status: ''
  });
  
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [summary, setSummary] = useState({
    totalQuotationValue: 0,
    totalGSTAmount: 0,
    totalAmountWithTax: 0
  });

  // Load customers and prospects for dropdown
  useEffect(() => {
    const loadCustomers = async () => {
      try {
        const [customerResponse, prospectResponse] = await Promise.all([
          customerMasterService.getAllCustomers({ limit: 1000 }),
          prospectMasterService.getAllProspects({ limit: 1000 })
        ]);
        
        let combinedCustomers = [];
        
        if (customerResponse && customerResponse.success) {
          combinedCustomers = [...(customerResponse.data || []).map(c => ({
            ...c,
            type: 'Customer',
            displayName: `${c.customerName} (${c.customerCode})`
          }))];
        }
        
        if (prospectResponse && prospectResponse.success) {
          const prospects = (prospectResponse.data?.prospects || prospectResponse.data || []).map(p => ({
            ...p,
            type: 'Prospect',
            displayName: `${p.prospectName} (${p.prospectCode})`,
            customerName: p.prospectName,
            customerCode: p.prospectCode
          }));
          combinedCustomers = [...combinedCustomers, ...prospects];
        }
        
        setCustomers(combinedCustomers);
      } catch (error) {
        console.error('Error loading customers/prospects:', error);
      }
    };
    loadCustomers();
  }, []);

  // Load report data
  useEffect(() => {
    loadReportData();
  }, [filters]);

  const loadReportData = async () => {
    setLoading(true);
    try {
      const response = await salesQuotationService.getAllSalesQuotations({
        page: 1,
        limit: 10000, // Get all records for report
        startDate: filters.fromDate,
        endDate: filters.toDate,
        customer: filters.customer || undefined,
        status: filters.status || undefined
      });

      if (response && response.success) {
        // Filter out Draft, Deleted (isActive: false), and Cancelled/Withdrawn statuses
        const filteredData = (response.data || []).filter(quotation => {
          const status = quotation.status || '';
          const isActive = quotation.isActive !== false; // Exclude deleted (isActive: false)
          // Exclude Draft, Withdrawn (cancelled), and inactive records
          return isActive && 
                 status !== 'Draft' && 
                 status !== 'Withdrawn' &&
                 status !== 'Cancelled';
        });
        
        const transformedData = filteredData.map(quotation => ({
          ...quotation,
          id: quotation._id || quotation.id
        }));
        setQuotations(transformedData);

        // Calculate summary totals
        const totals = transformedData.reduce((acc, quotation) => {
          acc.totalQuotationValue += quotation.totalValue || 0;
          acc.totalGSTAmount += quotation.totalTaxAmount || 0;
          acc.totalAmountWithTax += quotation.totalAmountWithTax || 0;
          return acc;
        }, { totalQuotationValue: 0, totalGSTAmount: 0, totalAmountWithTax: 0 });
        setSummary(totals);
      } else {
        toast.error('Failed to load sales quotation report');
      }
    } catch (error) {
      console.error('Error loading sales quotation report:', error);
      toast.error('Failed to load sales quotation report');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApplyFilter = () => {
    loadReportData();
  };

  const handleResetFilter = () => {
    const currentMonth = getCurrentMonthDates();
    setFilters({
      customer: '',
      fromDate: currentMonth.fromDate,
      toDate: currentMonth.toDate,
      status: ''
    });
  };

  const handleView = (quotationId) => {
    navigate(`/sales/sales-quotation/view/${quotationId}`);
  };

  const handlePrint = (quotation) => {
    // Open print view in new window
    const printUrl = `/sales/sales-quotation/print/${quotation.id || quotation._id}`;
    const printWindow = window.open(printUrl, '_blank', 'width=1200,height=800');
    if (!printWindow) {
      toast.error('Please allow popups to view the print preview');
    }
  };

  const handleDownload = (quotation) => {
    // TODO: Implement download functionality
    toast.info(`Downloading quotation: ${quotation.quotationNumber}`);
    console.log('Download quotation:', quotation);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Font sizes for report
  const reportFontSize = {
    label: '11px',
    input: '11px',
    footerLabel: '14px',
    footerValue: '20px'
  };

  // Define columns for DataTable
  const columns = [
    {
      key: 'quotationNumber',
      label: 'Quotation #',
      sortable: true
    },
    {
      key: 'quotationDate',
      label: 'Quotation Date',
      sortable: true,
      render: (value) => formatDate(value)
    },
    {
      key: 'validUntil',
      label: 'Valid Until',
      sortable: true,
      render: (value) => formatDate(value)
    },
    {
      key: 'customerName',
      label: 'Customer/Prospect',
      sortable: true
    },
    {
      key: 'totalValue',
      label: 'Quotation Value',
      sortable: true,
      render: (value) => `₹ ${formatCurrency(value)}`
    },
    {
      key: 'totalTaxAmount',
      label: 'GST Amount',
      sortable: true,
      render: (value) => `₹ ${formatCurrency(value)}`
    },
    {
      key: 'totalAmountWithTax',
      label: 'Total Amount',
      sortable: true,
      render: (value) => `₹ ${formatCurrency(value)}`
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => {
        const statusColors = {
          'Draft': '#6b7280',
          'Sent': '#3b82f6',
          'Under Review': '#f59e0b',
          'Negotiation': '#8b5cf6',
          'Approved': '#10b981',
          'Accepted': '#059669',
          'Rejected': '#ef4444',
          'Expired': '#9ca3af',
          'Withdrawn': '#dc2626'
        };
        return (
          <span style={{ 
            color: statusColors[value] || '#6b7280',
            fontWeight: '500'
          }}>
            {value || 'Draft'}
          </span>
        );
      }
    },
    {
      key: 'currency',
      label: 'Currency',
      sortable: true,
      render: (value) => value || 'INR'
    }
  ];

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Sales',
      path: '/sales',
      onClick: () => navigate('/sales')
    },
    {
      label: 'Reports',
      path: '/sales?tab=reports',
      onClick: () => navigate('/sales?tab=reports')
    },
    {
      label: 'Sales Quotation Report',
      path: '/sales/reports/sales-quotation',
      isCurrent: true
    }
  ];

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1 style={{ fontSize: '22px' }}>Sales Quotation Report</h1>
          <p style={{ fontSize: '12px' }}>View and analyze sales quotations</p>
        </div>
      </div>

      {/* Filters Section - Compact, one row */}
      <div style={{
        background: 'white',
        padding: '16px 20px',
        marginBottom: '20px',
        marginLeft: '0',
        marginRight: '0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        borderRadius: '0',
        maxWidth: '100%',
        overflow: 'hidden'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1.5fr auto auto', gap: '10px', alignItems: 'end' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" style={{ marginBottom: '4px' }}>Select Customer/Prospect</label>
            <select
              className="form-input"
              value={filters.customer}
              onChange={(e) => handleFilterChange('customer', e.target.value)}
              style={{ padding: '6px 10px' }}
            >
              <option value="">All Customers/Prospects</option>
              {customers.map(customer => (
                <option key={customer._id || customer.id} value={customer._id || customer.id}>
                  {customer.displayName || customer.customerName || customer.prospectName}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" style={{ marginBottom: '4px' }}>Status</label>
            <select
              className="form-input"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              style={{ padding: '6px 10px' }}
            >
              <option value="">All Status</option>
              <option value="Draft">Draft</option>
              <option value="Sent">Sent</option>
              <option value="Under Review">Under Review</option>
              <option value="Negotiation">Negotiation</option>
              <option value="Approved">Approved</option>
              <option value="Accepted">Accepted</option>
              <option value="Rejected">Rejected</option>
              <option value="Expired">Expired</option>
              <option value="Withdrawn">Withdrawn</option>
            </select>
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label required" style={{ marginBottom: '4px' }}>From Date</label>
            <input
              type="date"
              className="form-input"
              value={filters.fromDate}
              onChange={(e) => handleFilterChange('fromDate', e.target.value)}
              style={{ padding: '6px 10px' }}
            />
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label required" style={{ marginBottom: '4px' }}>To Date</label>
            <input
              type="date"
              className="form-input"
              value={filters.toDate}
              onChange={(e) => handleFilterChange('toDate', e.target.value)}
              style={{ padding: '6px 10px' }}
            />
          </div>

          <button
            className="btn-primary"
            onClick={handleApplyFilter}
            style={{ 
              padding: '6px 10px',
              backgroundColor: '#ec4899',
              borderColor: '#ec4899',
              borderRadius: '0',
              minWidth: 'auto'
            }}
            title="Apply Filter"
          >
            <Filter size={16} />
          </button>

          <button
            className="btn-secondary"
            onClick={handleResetFilter}
            style={{ 
              padding: '6px 10px',
              borderRadius: '0',
              minWidth: 'auto'
            }}
            title="Reset Filter"
          >
            <RotateCcw size={16} />
          </button>
        </div>
      </div>

      {/* Data Table */}
      <div style={{ 
        marginBottom: '80px', 
        marginLeft: '0', 
        marginRight: '0',
        overflowX: 'hidden',
        width: '100%',
        maxWidth: '100%'
      }}>
        <DataTable
          data={quotations}
          columns={columns}
          loading={loading}
          noCard={true}
          className="sales-quotation-report-table"
          pageSize={50}
          customActions={[
            {
              type: 'view',
              label: 'View',
              icon: <FileText size={16} />,
              onClick: handleView
            },
            {
              type: 'print',
              label: 'Print',
              icon: <Printer size={16} />,
              onClick: handlePrint
            },
            {
              type: 'download',
              label: 'Download',
              icon: <DownloadIcon size={16} />,
              onClick: handleDownload
            }
          ]}
        />
      </div>

      {/* Summary Footer - Fixed at bottom, matching table width */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: '314px', // Sidebar (250px) + left padding (64px)
        right: '64px', // Right padding to match sales-dashboard
        background: '#E8E1F5',
        padding: '14px 20px',
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '20px',
        boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
        zIndex: 100,
        borderRadius: '0',
        borderTop: '2px solid #E8E1F5'
      }}>
        <div>
          <div style={{ fontSize: reportFontSize.footerLabel, color: '#51418F', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span>→</span> Total Quotation Value
          </div>
          <div style={{ fontSize: reportFontSize.footerValue, fontWeight: '600', color: '#51418F' }}>
            ₹ {formatCurrency(summary.totalQuotationValue)}
          </div>
        </div>
        <div>
          <div style={{ fontSize: reportFontSize.footerLabel, color: '#51418F', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span>→</span> Total GST Amount
          </div>
          <div style={{ fontSize: reportFontSize.footerValue, fontWeight: '600', color: '#51418F' }}>
            ₹ {formatCurrency(summary.totalGSTAmount)}
          </div>
        </div>
        <div>
          <div style={{ fontSize: reportFontSize.footerLabel, color: '#51418F', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span>→</span> Total Amount with GST
          </div>
          <div style={{ fontSize: reportFontSize.footerValue, fontWeight: '600', color: '#51418F' }}>
            ₹ {formatCurrency(summary.totalAmountWithTax)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesQuotationReport;

